import React, { useState, useCallback, useEffect } from 'react';
import { useCollaboration } from '../../contexts/CollaborationContext';
import { SessionDialog } from './SessionDialog';
import { useSessionUrl } from '../../hooks/useSessionUrl';
import { useI18n } from '../../contexts/I18nContext';
import { useDialog } from '../../contexts/DialogContext';

interface CollaborationPanelProps {
  className?: string;
}

/**
 * Collaboration panel component - 툴바와 일체형 디자인
 */
export const CollaborationPanel: React.FC<CollaborationPanelProps> = ({ className = '' }) => {
  const { t } = useI18n();
  const dialog = useDialog();
  const { state, createSession, joinSession, leaveSession, kickUser } = useCollaboration();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showUserList, setShowUserList] = useState(false);

  // URL 기반 세션 관리
  const {
    sessionKeyFromUrl,
    updateUrlWithSession,
    clearSessionFromUrl,
    markUrlSessionHandled
  } = useSessionUrl();

  const isInSession = state.mode === 'collaborative' && state.session;

  // URL에 세션 키가 있으면 자동으로 다이얼로그 열기
  useEffect(() => {
    if (sessionKeyFromUrl && !isInSession) {
      setIsDialogOpen(true);
    }
  }, [sessionKeyFromUrl, isInSession]);

  // 세션에 참가하면 URL 업데이트
  useEffect(() => {
    if (state.session?.id) {
      updateUrlWithSession(state.session.id);
    }
  }, [state.session?.id, updateUrlWithSession]);

  const handleCreateSession = useCallback(
    (name: string) => {
      createSession(name);
    },
    [createSession]
  );

  const handleJoinSession = useCallback(
    (sessionId: string, userName: string) => {
      joinSession(sessionId, userName);
      markUrlSessionHandled(); // URL 기반 세션 처리 완료
    },
    [joinSession, markUrlSessionHandled]
  );

  // 세션 나가기 (URL도 정리)
  const handleLeaveSession = useCallback(() => {
    leaveSession();
    clearSessionFromUrl();
  }, [leaveSession, clearSessionFromUrl]);

  const handleCopySessionId = useCallback(() => {
    if (state.session?.id) {
      navigator.clipboard.writeText(state.session.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [state.session?.id]);

  const statusColor = state.connectionState === 'connected' ? '#10B981'
    : state.connectionState === 'connecting' ? '#F59E0B'
    : state.connectionState === 'error' ? '#EF4444' : '#9CA3AF';

  return (
    <>
      <div className="toolbar-divider"></div>

      {!isInSession ? (
        <button
          className="toolbar-btn"
          onClick={() => setIsDialogOpen(true)}
          title={t.collaboration.title}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span className="toolbar-label">{t.collaboration.collab}</span>
        </button>
      ) : (
        <>
          {/* 세션 상태 - 클릭하면 참여자 목록 */}
          <div style={{ position: 'relative' }}>
            <button
              className="toolbar-btn"
              onClick={() => setShowUserList(!showUserList)}
              title={t.collaboration.sessionInfo.replace('{id}', state.session?.id || '').replace('{count}', String(state.users.length))}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: statusColor,
                }} />
                {state.users.slice(0, 3).map((user, i) => (
                  <div
                    key={user.id}
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: user.color || '#3B82F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 600,
                      color: 'white',
                      marginLeft: i > 0 ? '-4px' : '2px',
                      border: '1.5px solid white',
                    }}
                  >
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                ))}
                {state.users.length > 3 && (
                  <span style={{ fontSize: '10px', marginLeft: '2px' }}>+{state.users.length - 3}</span>
                )}
              </div>
              <span className="toolbar-label">{t.collaboration.peopleCount.replace('{count}', String(state.users.length))}</span>
            </button>

            {/* 참여자 목록 드롭다운 */}
            {showUserList && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '8px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: '200px',
                  zIndex: 1000,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #E5E7EB', fontWeight: 600, fontSize: '12px', color: '#6B7280' }}>
                  {t.collaboration.participants} ({state.users.length})
                </div>
                {state.users.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      borderBottom: '1px solid #F3F4F6',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: user.color || '#3B82F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: 'white',
                        }}
                      >
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: '13px', color: '#374151' }}>
                        {user.name}
                        {user.id === state.currentUser?.id && (
                          <span style={{ color: '#9CA3AF', fontSize: '11px', marginLeft: '4px' }}>({t.collaboration.you})</span>
                        )}
                      </span>
                    </div>
                    {user.id !== state.currentUser?.id && (
                      <button
                        onClick={async () => {
                          if (await dialog.confirm(t.collaboration.kickConfirm.replace('{name}', user.name || ''))) {
                            kickUser(user.id);
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          fontSize: '11px',
                          color: '#EF4444',
                          backgroundColor: '#FEF2F2',
                          border: '1px solid #FECACA',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        title={t.collaboration.kick}
                      >
                        {t.collaboration.kick}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 복사 */}
          <button
            className="toolbar-btn"
            onClick={handleCopySessionId}
            title={copied ? t.collaboration.copied : t.collaboration.copySessionId}
            style={{ color: copied ? '#10B981' : undefined }}
          >
            {copied ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
            <span className="toolbar-label">{copied ? t.collaboration.copied : t.collaboration.copy}</span>
          </button>

          {/* 나가기 */}
          <button
            className="toolbar-btn"
            onClick={handleLeaveSession}
            title={t.collaboration.leaveSession}
            style={{ color: '#EF4444' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="toolbar-label">{t.collaboration.leave}</span>
          </button>
        </>
      )}

      <SessionDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          markUrlSessionHandled(); // 다이얼로그 닫을 때 URL 세션 처리 완료
        }}
        onCreateSession={handleCreateSession}
        onJoinSession={handleJoinSession}
        initialSessionId={sessionKeyFromUrl}
      />
    </>
  );
};

export default CollaborationPanel;
